/* ====================================================================
 * TAGC Practice Areas — canonical data source.
 *
 * Used by the landing page (<PracticeAreas />) AND by the dedicated
 * detail pages (Pharmaceuticals, Medical Devices). Keep single-source
 * so positioning language stays consistent everywhere.
 *
 * Every practice includes:
 *   - services[]              : offering list
 *   - industries[]            : industries the practice serves
 *   - regulations[]           : regulatory frameworks the practice engages
 *   - toxintelCapability      : the future ToxIntel workflow this maps to
 *   - availability            : "available" | "expert-led"
 *   - detailHref              : optional route to a dedicated page
 *
 * IMPORTANT positioning guardrails (per client brief):
 *   - Medical Devices: NO claims of FDA/MDR approval, certification, or
 *     notified-body services. Consulting language only.
 *   - Pharmaceuticals: expert-led consulting language, no automated
 *     software capability claims.
 *   - ToxIntel is the PLATFORM. Aᴳ is the CONSULTING practice.
 * ==================================================================== */

const PRACTICE_AREAS = [
  {
    id: 'product-safety',
    order: '01',
    title: 'Product Safety & Toxicological Assessment',
    tagline: 'Foundational scientific assessment across every regulated category we serve.',
    body:
      'Independent toxicological evaluation, exposure-based risk assessment, and defensible ' +
      'safety opinions — the scientific core of every Aᴳ engagement, regardless of product class. ' +
      'The practice explicitly supports multi-pathway and aggregate exposure evaluation across ' +
      'dermal, inhalation, oral, and incidental-ingestion routes; pathway aggregation is ' +
      'performed only when scientifically appropriate — not by summing unrelated estimates.',
    services: [
      'Ingredient Safety Assessments',
      'Formula Safety Assessments',
      'Toxicological Risk Assessment (TRA)',
      'Quantitative Risk Assessment (QRA)',
      'Exposure Assessment · dermal / inhalation / oral / incidental',
      'Multi-Pathway & Aggregate Exposure Assessment',
      'Route-Specific → Systemic → Aggregate → Risk Characterization',
      'Margin of Safety (MoS) · MOE · HQ / HI · cancer & non-cancer risk',
      'Population-specific & acute/chronic exposure scenarios',
      'Scientific Safety Opinions',
      'Board-Certified Toxicologist Review',
      'Product Stewardship Support',
      'Uncertainty characterization & documentation',
    ],
    industries: ['Cosmetics', 'Personal Care', 'Consumer Products', 'Home Fragrance & Air Care', 'Pharmaceuticals', 'Medical Devices', 'Industrial Chemicals'],
    regulations: ['FDA MoCRA', 'EU 1223/2009', 'REACH', 'IFRA', 'Prop 65', 'TSCA', 'US EPA guidance'],
    toxintelCapability: 'Exposure, MoS & Aggregate-Exposure workflows',
    availability: 'available',
    image: '/tagc/generated/v2_service_testing_pipette.jpg',
    imageAlt: 'Glass pipette with a forming droplet above a sample vial.',
  },
  {
    id: 'cosmetics-personal-care',
    order: '02',
    title: 'Cosmetics & Personal Care',
    tagline: 'The scientific foundation the practice scales from.',
    body:
      'Full-stack cosmetic and personal-care safety governance — from ingredient-level ' +
      'toxicology through Cosmetic Product Safety Reports, MoCRA readiness, and retailer ' +
      'compliance dossiers.',
    services: [
      'Cosmetic Product Safety Reports (CPSR)',
      'MoCRA Consulting',
      'EU Cosmetic Compliance',
      'IFRA Safety Assessment',
      'Product Information Files (PIF)',
      'Claims Support',
      'Pregnancy & Breastfeeding Safety Reviews',
      'Pediatric Safety Reviews',
      'Retail Compliance',
      'Ingredient Intelligence',
    ],
    industries: ['Skin Care', 'Hair Care', 'Fragrance', 'Color Cosmetics', 'Professional Beauty', 'Men’s Grooming'],
    regulations: ['FDA MoCRA', 'EU 1223/2009', 'SCCS Notes of Guidance', 'IFRA', 'Prop 65'],
    toxintelCapability: 'CPSR & MoCRA dossier automation',
    availability: 'available',
    image: '/tagc/generated/v2_macro_cream_swatch.jpg',
    imageAlt: 'Fingertip drawing a swatch of pearlescent cream on skin.',
  },
  {
    id: 'consumer-products',
    order: '03',
    title: 'Consumer Products',
    tagline: 'Exposure-based safety governance beyond beauty.',
    body:
      'Toxicological assessment for household, cleaning, air-care, pet, children’s, ' +
      'lifestyle, and wellness products — grounded in the same exposure-based methodology ' +
      'that powers our cosmetic practice. Home-fragrance and air-care programs are served ' +
      'through the dedicated Home Fragrance & Air Care vertical.',
    services: [
      'Household Cleaning Products',
      'Air Care',
      'Laundry Products',
      'Surface Cleaners',
      'Pet Products',
      'Children’s Products',
      'Lifestyle Products',
      'Wellness Products',
    ],
    industries: ['Household Products', 'Cleaning Products', 'Air Care', 'Pet Products', 'Children’s Products'],
    regulations: ['CPSC', 'FIFRA', 'Prop 65', 'OSHA HazCom', 'REACH'],
    toxintelCapability: 'Multi-category exposure modelling',
    availability: 'available',
    image: '/tagc/generated/v2_service_prop65_label.jpg',
    imageAlt: 'Fingertip smoothing a warning label onto a matte white consumer product.',
  },
  {
    id: 'pharmaceuticals',
    order: '04',
    title: 'Pharmaceuticals',
    tagline: 'Expert-led pharmaceutical toxicology and impurity science.',
    body:
      'Consulting on pharmaceutical impurity risk, occupational exposure limits, ' +
      'nitrosamine assessment, and toxicological support for drug development. Expert-led — ' +
      'not automated software capabilities.',
    services: [
      'PDE Derivation',
      'Occupational Exposure Limit (OEL) Support',
      'ICH M7 Scientific Review',
      'Nitrosamine Risk Assessment',
      'Impurity Qualification',
      'Pharmaceutical Toxicology',
      'Extractables & Leachables Review',
      'Occupational Toxicology',
    ],
    industries: ['Drug Products', 'Drug Substances', 'Manufacturing', 'Pharmaceutical Development'],
    regulations: ['ICH M7', 'ICH Q3A/B/C/D', 'EMA PDE Guideline', 'USP <232>/<233>', 'OSHA'],
    toxintelCapability: 'ICH M7 & PDE workflow templates',
    availability: 'expert-led',
    detailHref: '/practice/pharmaceuticals',
    image: '/tagc/generated/v2_macro_capsule_in_hand.jpg',
    imageAlt: 'Two amber capsules resting in an open palm.',
  },
  {
    id: 'medical-devices',
    order: '05',
    title: 'Medical Devices',
    tagline: 'Toxicological evaluation and biological-evaluation scientific support.',
    body:
      'Expert-led toxicological evaluation, biological evaluation planning, and material ' +
      'risk assessment supporting medical-device programs. Scientific consulting only — ' +
      'we do not provide certification, notified-body services, or regulatory decisions.',
    services: [
      'ISO 10993 Toxicological Risk Assessment',
      'Biological Evaluation Planning',
      'Chemical Characterization Review',
      'Material Risk Assessment',
      'Extractables & Leachables',
      'Patient Exposure Assessment',
      'FDA Scientific Support',
      'MDR Scientific Support',
      'Device Material Safety Review',
    ],
    industries: ['Patient Contact Devices', 'Combination Products', 'Medical Packaging', 'Implantable Devices', 'Disposable Devices'],
    regulations: ['ISO 10993 series', 'FDA guidance (scientific support)', 'EU MDR (scientific support)', 'ISO 14971', 'USP <661.1>/<661.2>'],
    toxintelCapability: 'ISO 10993 TRA & E&L workflow templates',
    availability: 'expert-led',
    detailHref: '/practice/medical-devices',
    image: '/tagc/generated/v3_practice_medical_devices.jpg',
    imageAlt: 'Medical-device material samples — silicone tubing, titanium coupon, polymer film — with gloved fingertips.',
    positioningDisclaimer:
      'Aᴳ provides toxicological evaluations, biological-evaluation scientific support, and risk ' +
      'assessment for medical-device programs. Aᴳ does not provide FDA approval, EU MDR ' +
      'approval, certification, notified-body services, or regulatory-authority decisions.',
  },
  {
    id: 'regulatory-science',
    order: '06',
    title: 'Regulatory Science',
    tagline: 'Cross-jurisdiction regulatory strategy grounded in defensible science.',
    body:
      'Regulatory scientific consulting across chemistry, exposure, and hazard-communication ' +
      'frameworks — from California Prop 65 through REACH, CLP, and global GHS.',
    services: [
      'Proposition 65',
      'REACH',
      'CLP',
      'OSHA Hazard Communication',
      'GHS Classification',
      'SDS Authoring',
      'Global Regulatory Strategy',
      'Scientific Regulatory Support',
    ],
    industries: ['Cosmetics', 'Consumer Products', 'Industrial Chemicals', 'Pharmaceuticals'],
    regulations: ['Prop 65', 'REACH (EC) 1907/2006', 'CLP (EC) 1272/2008', 'OSHA HCS', 'UN GHS'],
    toxintelCapability: 'Prop 65 & GHS classification workflows',
    availability: 'available',
    image: '/tagc/generated/v2_service_mocra_stamping.jpg',
    imageAlt: 'Hands pressing a seal stamp onto a regulatory document.',
  },
  {
    id: 'environmental-toxicology',
    order: '07',
    title: 'Ecotoxicology & Environmental Fate',
    tagline: 'Ecological receptors, environmental fate, persistence, and bioaccumulation.',
    body:
      'Scientific consulting focused on non-human environmental receptors: aquatic and ' +
      'terrestrial ecotoxicology, environmental fate and transport, persistence, ' +
      'bioaccumulation, and PBT / vPvB / PMT screening. Human-health risk assessment ' +
      'is served through the dedicated EPA Human Health & Environmental Risk ' +
      'Assessment practice.',
    services: [
      'Aquatic & terrestrial ecotoxicology',
      'Environmental fate & transport',
      'Persistence & degradation assessment',
      'Bioaccumulation assessment',
      'PBT · vPvB · PMT screening',
      'Environmental hazard characterization',
      'Aquatic toxicity evaluation',
      'PEC / PNEC & environmental risk characterization',
      'Environmental exposure & fate data review',
      'Environmental data-gap assessment',
    ],
    industries: ['Sustainability Programs', 'Chemical Stewardship', 'Environmental Risk', 'Industrial Chemicals'],
    regulations: ['REACH Annex XIII', 'ECHA Guidance', 'OECD Test Guidelines', 'Stockholm Convention'],
    toxintelCapability: 'PBT / vPvB / PMT screening workflow',
    availability: 'expert-led',
    image: '/tagc/generated/v3_practice_ecotoxicology.jpg',
    imageAlt: 'Freshwater pond surface with lily pad and reed, hand holding a water sample vial at the edge.',
  },
  {
    id: 'scientific-intelligence',
    order: '08',
    title: 'Scientific Intelligence',
    tagline: 'Independent scientific review, evidence synthesis, and expert-witness support.',
    body:
      'Literature reviews, weight-of-evidence assessments, scientific due diligence, and ' +
      'independent scientific review — for M&A diligence, litigation, and governance ' +
      'programs that require defensible scientific opinion.',
    services: [
      'Literature Reviews',
      'Scientific Evidence Review',
      'Weight-of-Evidence Assessments',
      'Systematic Literature Review',
      'Scientific Due Diligence',
      'Expert Witness Support',
      'Scientific Governance',
      'Independent Scientific Review',
    ],
    industries: ['Legal & Enforcement', 'M&A Diligence', 'Insurance & Reinsurance', 'Retail Governance'],
    regulations: ['Framework-agnostic — evidence synthesis across all applicable jurisdictions'],
    toxintelCapability: 'Evidence-synthesis & WoE workspace',
    availability: 'expert-led',
    image: '/tagc/generated/v2_macro_raw_botanical.jpg',
    imageAlt: 'Fingertips brushing pale botanical powder on cream parchment.',
  },
  {
    id: 'home-fragrance-air-care',
    order: '09',
    title: 'Home Fragrance & Air Care',
    tagline: 'Toxicology, exposure, and regulatory support across candles, diffusers, sprays, plug-ins, wax melts, and incense.',
    body:
      'A dedicated scientific practice for the home-fragrance and air-care category — ' +
      'candles, scented candles, reed diffusers, room sprays, plug-in devices, wax melts, ' +
      'incense, and adjacent products. Inhalation-first exposure characterization combined ' +
      'with dermal and incidental-oral pathways where appropriate, aligned to IFRA use ' +
      'categories, GHS/CLP hazard classification, Prop 65, and EU/GB PCN·UFI readiness. ' +
      'Aᴳ does not issue IFRA certification and does not act as a third-party certification ' +
      'body — services are independent scientific consulting and documentation review.',
    services: [
      'Toxicological safety assessment',
      'Ingredient & formulation review',
      'Fragrance safety assessment',
      'Inhalation exposure assessment',
      'Multi-pathway exposure assessment',
      'IFRA compliance review',
      'GHS / CLP classification',
      'SDS preparation & review',
      'California Proposition 65 assessment',
      'EU / GB regulatory support',
      'UFI / PCN readiness',
      'ASTM-related candle safety support',
      'Environmental hazard assessment',
      'Claims review',
      'Product stewardship',
    ],
    industries: ['Candles', 'Scented Candles', 'Reed Diffusers', 'Room Sprays', 'Plug-ins', 'Wax Melts', 'Incense', 'Consumer Products'],
    regulations: ['IFRA Standards', 'EU CLP', 'EU CLP CLP-PCN (UFI)', 'GB CLP', 'Prop 65', 'ASTM F2417 / F2179 (candle safety)'],
    toxintelCapability: 'Inhalation & Aggregate Exposure workflows',
    availability: 'available',
    detailHref: '/practice/home-fragrance-air-care',
    image: '/tagc/generated/v3_practice_home_fragrance.jpg',
    imageAlt: 'Editorial still life of a lit ivory candle, matte reed diffuser, and amber wax melts on cream paper.',
    positioningDisclaimer:
      'Aᴳ provides independent toxicological evaluation, exposure characterization, and ' +
      'regulatory scientific consulting for home-fragrance and air-care products. Aᴳ does ' +
      'not perform IFRA certification, does not act as a notified body, and does not issue ' +
      'third-party regulatory approvals.',
  },
  {
    id: 'epa-hhera',
    order: '10',
    title: 'EPA Human Health & Environmental Risk Assessment',
    tagline: 'EPA-aligned human-health risk assessment: hazard identification through risk characterization.',
    body:
      'Independent human-health risk-assessment consulting using EPA-aligned scientific ' +
      'methodology — hazard identification, dose-response, exposure assessment, and risk ' +
      'characterization for site-, chemical-, occupational-, and product-related HHRA. ' +
      'Multi-pathway and aggregate exposure across dermal, inhalation, and oral routes; ' +
      'drinking-water, soil, dietary, and air pathways where applicable. Scientific ' +
      'interpretation of authoritative EPA toxicology information (IRIS, PPRTV, Regional ' +
      'Screening Levels, Exposure Factors Handbook). Aᴳ is not EPA and does not act on ' +
      'EPA’s behalf.',
    services: [
      'Human-health risk assessment (site · chemical · occupational · product)',
      'Hazard identification',
      'Dose-response assessment',
      'RfD / RfC / POD / BMDL derivation & review',
      'Exposure assessment (dermal · inhalation · oral)',
      'Multi-pathway & aggregate exposure',
      'Drinking-water · soil · dietary · air pathways',
      'Acute & chronic exposure scenarios',
      'Sensitive populations & lifestages',
      'HQ · HI · MOE',
      'LADD & cancer-risk characterization',
      'Uncertainty analysis',
      'Application of IRIS / PPRTV / RSL / EPA Exposure Factors Handbook values',
      'TSCA-related toxicology support',
      'Weight-of-evidence evaluation',
      'Read-across & data-gap assessment',
    ],
    industries: ['Industrial Chemicals', 'Consumer Products', 'Environmental', 'Sustainability Programs', 'Chemical Stewardship'],
    regulations: ['EPA-aligned scientific methodology', 'TSCA', 'FIFRA', 'CERCLA', 'IRIS · PPRTV · RSL · Exposure Factors (interpretation)'],
    toxintelCapability: 'HHRA & Aggregate-Exposure workflows',
    availability: 'expert-led',
    detailHref: '/practice/epa-human-health-environmental-risk',
    image: '/tagc/generated/v3_practice_epa_hhra.jpg',
    imageAlt: 'Flat-lay of a dose-response curve, exposure-pathway overlay, and fountain pen on a slate desk.',
    positioningDisclaimer:
      'Aᴳ provides independent scientific consulting using EPA-aligned human-health risk-' +
      'assessment methods and interprets authoritative EPA toxicology information (IRIS, ' +
      'PPRTV, RSL, Exposure Factors Handbook). Aᴳ is not EPA, does not act on EPA’s behalf, ' +
      'does not issue EPA regulatory decisions, and does not operate live EPA database ' +
      'integrations on this website.',
  },
];

export default PRACTICE_AREAS;
